import { useState } from 'react';

import { FileDown } from 'lucide-react';

import Button from '../../common/Button';
import { type TypeActualDataSchemaFile } from '../../common/dataTypes';
import { useGetDownloadingActualDataTemplate } from '../../hooks/tanstack/useActualDataHook';
import { useGetRouteParams } from '../../hooks/tanstack/useUtilsHook';
import DownloadTemplatesDialog from '../dataset/DownloadTemplatesDialog';

type DownloadActualDataTemplatesDialogProps = {
  files: TypeActualDataSchemaFile[];
  trigger?: {
    label?: React.ReactNode;
    style?: React.CSSProperties;
  };
};

const DownloadingLink = ({ file }: { file: TypeActualDataSchemaFile }) => {
  const { orgId, projectId } = useGetRouteParams();

  const { data: downloadActualDataFile } = useGetDownloadingActualDataTemplate({
    orgId,
    projectId,
    templateName: file.template,
  });

  return (
    <div className="flex justify-between items-center">
      <p className="w-[500px] truncate">{file.name}</p>
      {downloadActualDataFile?.fileLink && (
        <a
          href={downloadActualDataFile.fileLink}
          download={downloadActualDataFile.filename}
          className="cursor-pointer flex items-center"
        >
          <FileDown size={20} color="#2196F3" />
        </a>
      )}
    </div>
  );
};

const DownloadActualDataTemplatesDialog = ({
  files,
}: DownloadActualDataTemplatesDialogProps) => {
  const [open, setOpen] = useState(false);

  const { orgId, projectId } = useGetRouteParams();

  const { data: downloadActualDataFile, isLoading: isLoadingDownload } =
    useGetDownloadingActualDataTemplate({
      orgId,
      projectId,
      templateName: 'all',
    });

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <DownloadTemplatesDialog
      open={open}
      handleOpen={handleOpen}
      handleClose={handleClose}
    >
      <>
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold">Download Template(s)</h1>
          <Button
            asChild
            variant="outlined_blue"
            disabled={isLoadingDownload}
            loading={isLoadingDownload}
          >
            <a
              href={downloadActualDataFile?.fileLink}
              download={downloadActualDataFile?.filename}
              className="flex gap-1 cursor-pointer items-center"
            >
              <FileDown size={20} />
              Download all
            </a>
          </Button>
        </div>
        <div className="flex flex-col gap-4">
          <p className="font-bold text-[#666666]">Name</p>
          {files?.map((file, index) => (
            <DownloadingLink key={index} file={file} />
          ))}
        </div>
      </>
    </DownloadTemplatesDialog>
  );
};

export default DownloadActualDataTemplatesDialog;
